import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  TextField,
  Alert
} from '@mui/material';
import { Sparkles, Plus, ArrowLeft } from 'lucide-react';
import AdminModal from './AdminModal';
import { adminPanelPlaceholderSx, AdminPanelLoading } from './adminPanelContent';
import { WORKFLOW_CATEGORIES, workflowCategoryLabel } from '../constants/workflowCategories';
import { useWorkflows } from '../context/WorkflowsContext';

const CategoryIcon = ({ cat }) => {
  const Icon = cat.Icon;
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        bgcolor: 'rgba(139, 92, 246, 0.14)',
        color: 'primary.light'
      }}
    >
      <Icon size={20} strokeWidth={2} />
    </Box>
  );
};

export default function WorkflowsStub() {
  const navigate = useNavigate();
  const { workflowsByCategory, addWorkflow, loading, loadError, refreshWorkflows } = useWorkflows();
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addCategoryLocked, setAddCategoryLocked] = useState(null);
  const [newWorkflowCategory, setNewWorkflowCategory] = useState('automation');
  const [newWorkflowName, setNewWorkflowName] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState('');

  const openAddFromOverview = useCallback(() => {
    setAddCategoryLocked(null);
    setNewWorkflowCategory('automation');
    setNewWorkflowName('');
    setAddOpen(true);
  }, []);

  const openAddFromCategory = useCallback((categoryId) => {
    setAddCategoryLocked(categoryId);
    setNewWorkflowName('');
    setAddOpen(true);
  }, []);

  const closeAdd = useCallback(() => {
    setAddOpen(false);
    setAddCategoryLocked(null);
    setNewWorkflowName('');
    setAddError('');
    setAddSubmitting(false);
  }, []);

  const submitAdd = useCallback(async () => {
    const cat = addCategoryLocked ?? newWorkflowCategory;
    if (!cat) return;
    const name = newWorkflowName.trim();
    if (!name) return;
    setAddError('');
    setAddSubmitting(true);
    try {
      await addWorkflow(cat, name);
      closeAdd();
    } catch (e) {
      setAddError(e.message || 'Failed to create workflow');
    } finally {
      setAddSubmitting(false);
    }
  }, [addCategoryLocked, newWorkflowCategory, newWorkflowName, addWorkflow, closeAdd]);

  const listForCategory = useMemo(
    () => (selectedCategoryId ? workflowsByCategory[selectedCategoryId] || [] : []),
    [selectedCategoryId, workflowsByCategory]
  );

  const selectedCatMeta = useMemo(
    () => WORKFLOW_CATEGORIES.find((c) => c.id === selectedCategoryId),
    [selectedCategoryId]
  );

  const HeaderIcon = selectedCatMeta ? selectedCatMeta.Icon : Sparkles;

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          {selectedCategoryId ? (
            <IconButton
              size="small"
              aria-label="Back to categories"
              onClick={() => setSelectedCategoryId(null)}
              sx={{ color: 'text.secondary', flexShrink: 0 }}
            >
              <ArrowLeft size={18} strokeWidth={2} />
            </IconButton>
          ) : null}
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              color: selectedCategoryId ? 'text.secondary' : 'warning.light'
            }}
          >
            <HeaderIcon size={selectedCategoryId ? 18 : 22} strokeWidth={2} />
          </Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
            {selectedCategoryId ? workflowCategoryLabel(selectedCategoryId) : 'Workflows'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<Plus size={18} strokeWidth={2} />}
          onClick={() =>
            selectedCategoryId ? openAddFromCategory(selectedCategoryId) : openAddFromOverview()
          }
          sx={{ flexShrink: 0 }}
          disabled={loading || (!!loadError && !loading)}
        >
          Add workflow
        </Button>
      </Box>

      {loadError ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refreshWorkflows()}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : null}

      {loading ? (
        <AdminPanelLoading label="Loading workflows…" />
      ) : !selectedCategoryId ? (
        <Grid container spacing={1.5}>
          {WORKFLOW_CATEGORIES.map((cat) => (
            <Grid item xs={12} sm={6} md={4} key={cat.id}>
              <Paper
                variant="outlined"
                onClick={() => setSelectedCategoryId(cat.id)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  borderColor: 'divider',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <Box display="flex" alignItems="flex-start" gap={1.75}>
                  <CategoryIcon cat={cat} />
                  <Box minWidth={0} flex={1}>
                    <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                      {cat.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {(workflowsByCategory[cat.id] || []).length}{' '}
                      {(workflowsByCategory[cat.id] || []).length === 1 ? 'workflow' : 'workflows'}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box>
          {listForCategory.length === 0 ? (
            <Box sx={adminPanelPlaceholderSx}>
              <Typography variant="body2" color="text.secondary">
                No workflows in this category yet.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={1.5}>
              {listForCategory.map((wf) => (
                <Grid item xs={12} sm={6} md={4} key={wf.id}>
                  <Paper
                    variant="outlined"
                    onClick={() => navigate(`/workflows/${encodeURIComponent(wf.id)}`)}
                    sx={{
                      p: 2,
                      borderColor: 'divider',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s, background-color 0.15s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {wf.name}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      <AdminModal
        open={addOpen}
        onClose={closeAdd}
        title="Add workflow"
        maxWidth="xs"
        actions={
          <>
            <Button onClick={closeAdd} disabled={addSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={submitAdd}
              disabled={!newWorkflowName.trim() || addSubmitting}
            >
              {addSubmitting ? 'Saving…' : 'Add'}
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2}>
          {addError ? (
            <Alert severity="error" onClose={() => setAddError('')}>
              {addError}
            </Alert>
          ) : null}
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={newWorkflowName}
            onChange={(e) => setNewWorkflowName(e.target.value)}
            autoFocus
            placeholder="e.g. Daily digest"
          />
          {addCategoryLocked ? (
            <Typography variant="body2" color="text.secondary">
              Category:{' '}
              <Typography component="span" color="text.primary" fontWeight={600}>
                {workflowCategoryLabel(addCategoryLocked)}
              </Typography>
            </Typography>
          ) : (
            <FormControl size="small" fullWidth>
              <InputLabel id="wf-add-category">Category</InputLabel>
              <Select
                labelId="wf-add-category"
                label="Category"
                value={newWorkflowCategory}
                onChange={(e) => setNewWorkflowCategory(e.target.value)}
              >
                {WORKFLOW_CATEGORIES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </AdminModal>
    </Box>
  );
}
